import React, { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import './ImageCropper.css';

const ImageCropper = ({ 
  imageSrc, 
  onCropComplete, 
  onCancel,
  onChangeImage, // Callback to trigger file selection
  cropShape = 'rect', // 'rect' or 'round'
  aspect = null, // null for free aspect, or number like 16/9
  imageType = 'profile' // 'profile' or 'banner'
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [imageKey, setImageKey] = useState(0);

  // Reset crop and zoom when image changes
  useEffect(() => {
    // Reset all state when image source changes (including when it becomes null)
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    // Increment key to force Cropper component to completely re-render
    if (imageSrc) {
      setImageKey(prev => prev + 1);
    }
  }, [imageSrc]);

  const onCropChange = useCallback((crop) => {
    setCrop(crop);
  }, []);

  const onZoomChange = useCallback((zoom) => {
    setZoom(zoom);
  }, []);

  const onCropAreaChange = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });

  const getCroppedImg = async (imageSrc, pixelCrop) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    // Set canvas size to match cropped area
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // Draw the cropped image
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    // Convert canvas to blob
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        resolve(blob);
      }, 'image/jpeg', 0.95);
    });
  };

  const handleCropComplete = async () => {
    if (!croppedAreaPixels) {
      return;
    }

    try {
      const croppedImageBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
      
      // Convert blob to File object
      const file = new File([croppedImageBlob], 'cropped-image.jpg', {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });

      onCropComplete(file);
    } catch (error) {
      console.error('Error cropping image:', error);
      alert('Failed to crop image. Please try again.');
    }
  };

  // Don't render if no image source
  if (!imageSrc) {
    return (
      <div className="image-cropper-container">
        <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
          Loading image...
        </div>
      </div>
    );
  }

  return (
    <div className="image-cropper-container">
      <div className="image-cropper-wrapper">
        <div className="image-cropper-area">
          <Cropper
            key={`cropper-internal-${imageKey}-${imageSrc ? imageSrc.substring(0, 50) : 'no-image'}`}
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            cropShape={cropShape}
            onCropChange={onCropChange}
            onZoomChange={onZoomChange}
            onCropComplete={onCropAreaChange}
            style={{
              containerStyle: {
                width: '100%',
                height: '100%',
                position: 'relative',
                background: '#1a1a1a',
              },
              cropAreaStyle: {
                border: '3px solid #7a5fb8', /* Increased from 2px for better visibility */
                boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)', /* Add overlay effect */
              },
            }}
          />
        </div>
      </div>

      <div className="image-cropper-controls">
        {imageType === 'banner' && (
          <div className="crop-info-banner">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <circle cx="8.5" cy="8.5" r="1.5"></circle>
              <polyline points="21,15 16,10 5,21"></polyline>
            </svg>
            <span>Adjust the crop area to select which part of your image will be visible as the cover (4:1 ratio - Recommended: 1584 × 396px)</span>
          </div>
        )}
        <div className="zoom-control">
          <label className="zoom-label">Zoom</label>
          <input
            type="range"
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(parseFloat(e.target.value))}
            className="zoom-slider"
          />
          <span className="zoom-value">{Math.round(zoom * 100)}%</span>
        </div>

        <div className="crop-actions">
          {onChangeImage && (
            <button
              className="crop-btn crop-btn-change"
              onClick={onChangeImage}
              type="button"
              title="Change Image"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
              <span className="btn-text">Change Image</span>
            </button>
          )}
          <button
            className="crop-btn crop-btn-cancel"
            onClick={onCancel}
            type="button"
            title="Cancel"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
            <span className="btn-text">Cancel</span>
          </button>
          <button
            className="crop-btn crop-btn-apply"
            onClick={handleCropComplete}
            type="button"
            title="Apply Crop"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            <span className="btn-text">Apply Crop</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper;

